import { Equal, Option, Result } from "effect";
import * as AsyncData from "foldkit/asyncData";
import { evo } from "foldkit/struct";

import { settle, start } from "../../api/asyncData";
import { asOfTag } from "../../api/client";
import { getEvents } from "../../api/commands/events";
import { asOfOf } from "../../app/routes";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import type { Events, Model } from "./model";

export const FetchEvents = getEvents.command(
	Message.CompletedFetchEvents,
	asOfTag,
);

type Return = PageReturn<Model, Message>;

const fetch = (model: Model, shared: Shared, cursor: number) =>
	FetchEvents({
		search: model.search,
		cursor,
		asOf: shared.asOf,
		generation: shared.generation,
	});

/** A fresh first page: v1 refetches on every keystroke, with no debounce. */
const load = (model: Model, shared: Shared): Return => ({
	model: evo(model, {
		events: () => AsyncData.Loading(),
		loadingMore: () => false,
	}),
	commands: [fetch(model, shared, 0)],
});

const toEvents = (
	page: Readonly<{ data: Events["data"]; nextCursor?: number | undefined }>,
): Events => ({
	data: page.data,
	nextCursor: Option.fromNullishOr(page.nextCursor),
});

export const page: Page<Model, Message> = {
	init: (_route, shared) =>
		load(
			{
				page: "AuditLog",
				search: "",
				events: AsyncData.Idle(),
				loadingMore: false,
			},
			shared,
		),
	changedRoute: (model, previous, next, shared) =>
		Equal.equals(asOfOf(previous), asOfOf(next))
			? { model }
			: load(model, shared),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			ChangedSearch: ({ search }) =>
				load(evo(model, { search: () => search }), shared),
			ClickedLoadMore: () => {
				const next = AsyncData.isSuccess(model.events)
					? model.events.data.nextCursor
					: Option.none();
				return Option.match(next, {
					onNone: () => ({ model }),
					onSome: (cursor) => ({
						model: evo(model, { loadingMore: () => true }),
						commands: [fetch(model, shared, cursor)],
					}),
				});
			},
			CompletedFetchEvents: ({ search, cursor, asOf, generation, result }) => {
				if (
					generation !== shared.generation ||
					!Equal.equals(asOf, shared.asOf) ||
					search !== model.search
				) {
					return { model };
				}
				if (cursor === 0) {
					return {
						model: evo(model, {
							events: (events) => settle(events, Result.map(result, toEvents)),
						}),
					};
				}
				// Load more: append to the flattened list; a failure leaves the list as it was.
				return {
					model: evo(model, {
						loadingMore: () => false,
						events: (events) =>
							Result.isSuccess(result) && AsyncData.isSuccess(events)
								? AsyncData.succeed({
										data: [...events.data.data, ...result.success.data],
										nextCursor: Option.fromNullishOr(result.success.nextCursor),
									})
								: events,
					}),
				};
			},
		}),
};
